import { useMemo } from 'react';
import Layout from '@/layouts/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import ReviewStep from '@/pages/patients/journals/components/ReviewStep';
import { CalendarDays, ClipboardList, Sparkles, Thermometer, Zap, ListTree, ShieldAlert } from 'lucide-react';
import type { JournalCardPayload, JournalCardResponsePayload, JournalEntry } from '@domain/journal';
import { Heading } from '@/components/ui/heading';
import { GaugePill } from '@/components/journal/GaugePill';
import { breadcrumbs } from '@/components/ui/breadcrumb';

type PatientSummary = {
  id: number;
  display_name: string;
};

type SummaryField = {
  key: string;
  label: string;
  value: string;
};

type EmotionSummary = {
  label?: string | null;
  intensity?: number | null;
};

type ClinicalSummary = {
  version?: string | null;
  date?: string | null;
  headline?: string | null;
  narrative?: string | null;
  fields: SummaryField[];
  emotion?: EmotionSummary | null;
  patterns: string[];
};

type JournalWarmup = {
  summary?: string | null;
  moodLevel?: number | null;
  energyLevel?: number | null;
  moodFeedback?: string | null;
  energyFeedback?: string | null;
};

type JournalMetrics = {
  createdAt?: string | null;
  completedAt?: string | null;
  writingStartedAt?: string | null;
  writingSubmittedAt?: string | null;
  writingDurationSeconds?: number | null;
  writingWordCount?: number | null;
  alertTherapist?: boolean | null;
};

type JournalCard = {
  id: number;
  journalCardId: number;
  responseValue: string;
  createdAt?: string | null;
  rationale?: Record<string, unknown> | null;
  card?: {
    id: number;
    text: string;
    category?: string | null;
    clinicalWeight?: number | null;
    tags?: string[] | null;
  } | null;
};

type JournalWriting = {
  finalText?: string | null;
  draftBody?: string | null;
  draftRaw?: Record<string, unknown> | null;
  commitmentText?: string | null;
  alertTherapist?: boolean | null;
};

type JournalDetails = {
  warmup?: JournalWarmup | null;
  metrics?: JournalMetrics | null;
  cards?: JournalCard[];
  writing?: JournalWriting | null;
};

type CoachMessage = {
  role: string;
  content: string;
  created_at: string;
};

type EntryDetail = {
  id: number;
  date: string;
  status: string;
  completed_at?: string | null;
  mood_level?: number | null;
  energy_level?: number | null;
  emotion?: EmotionSummary | null;
  summary?: ClinicalSummary | null;
  review_failed_at?: string | null;
  summary_unavailable_message?: string | null;
  safety_blocked?: boolean;
  coach_messages?: CoachMessage[];
  journal: JournalDetails;
};

type Props = {
  patient: PatientSummary;
  entry: EntryDetail;
};

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleString('it-IT', { dateStyle: 'medium' });
}

function hasSummaryContent(summary?: ClinicalSummary | null) {
  if (!summary) return false;
  return (
    (summary.headline && summary.headline.trim().length > 0) ||
    (summary.narrative && summary.narrative.trim().length > 0) ||
    summary.fields.length > 0 ||
    !!summary.emotion?.label ||
    typeof summary.emotion?.intensity === 'number' ||
    summary.patterns.length > 0
  );
}

const cleanString = (value?: string | null): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toCardPayload = (card: JournalCard, fallbackId: number): JournalCardPayload => {
  const payload = (card.card ?? {}) as Record<string, unknown>;
  const payloadId = typeof payload['id'] === 'number' ? (payload['id'] as number) : undefined;
  const payloadText = cleanString(payload['text'] as string | null | undefined);
  const payloadCategory = cleanString(payload['category'] as string | null | undefined);
  return {
    id:
      payloadId ??
      (typeof card.journalCardId === 'number' ? card.journalCardId : undefined) ??
      fallbackId,
    text: payloadText ?? 'Card',
    category: payloadCategory ?? 'Generale',
    selected: card.responseValue === 'yes',
  };
};

const toCardResponses = (cards: JournalCard[]): JournalCardResponsePayload[] =>
  cards.map((card, index) => ({
    id: typeof card.id === 'number' ? card.id : index,
    journalCardId: card.journalCardId,
    responseValue: card.responseValue,
    createdAt: card.createdAt ?? null,
    card: toCardPayload(card, index),
  }));

const toReviewEntry = (detail: EntryDetail): JournalEntry => {
  const journal = detail.journal ?? ({} as JournalDetails);
  const metrics = journal.metrics ?? {};
  const warmup = journal.warmup ?? ({} as JournalWarmup);
  const cards = Array.isArray(journal.cards) ? journal.cards : [];
  const writing = journal.writing ?? ({} as JournalWriting);
  const currentStep: JournalEntry['currentStep'] =
    (detail.status as JournalEntry['status']) === 'completed' ? 'completed' : 'writing';

  return {
    id: detail.id,
    date: detail.date,
    status: (detail.status as JournalEntry['status']) ?? 'in_progress',
    currentStep,
    createdAt: metrics.createdAt ?? detail.date,
    completedAt: metrics.completedAt ?? detail.completed_at ?? null,
    warmup: {
      moodLevel: typeof warmup.moodLevel === 'number' ? warmup.moodLevel : null,
      energyLevel: typeof warmup.energyLevel === 'number' ? warmup.energyLevel : null,
      moodFeedback: cleanString(warmup.moodFeedback ?? null),
      energyFeedback: cleanString(warmup.energyFeedback ?? null),
    },
    hand: cards.map((card, index) => toCardPayload(card, index)),
    writing: {
      writingStartedAt: metrics.writingStartedAt ?? null,
      writingSubmittedAt: metrics.writingSubmittedAt ?? null,
      writingWordCount: metrics.writingWordCount ?? null,
      writingDurationSeconds: metrics.writingDurationSeconds ?? null,
      commitmentText: cleanString(writing.commitmentText ?? null),
      alertTherapist: Boolean(metrics.alertTherapist ?? writing.alertTherapist),
      draftText: writing.draftRaw ?? null,
      finalText: cleanString(writing.finalText ?? null),
    },
    chat: null,
    cardResponses: toCardResponses(cards),
    updatedAt:
      metrics.writingSubmittedAt ??
      metrics.completedAt ??
      metrics.createdAt ??
      detail.completed_at ??
      detail.date,
  };
};

export default function TherapistJournalShow({ patient, entry }: Props) {
  const summary = entry.summary ?? null;
  const summaryHasContent = hasSummaryContent(summary);
  const summaryUnavailable = !!entry.review_failed_at;
  const reviewEntry = useMemo(() => toReviewEntry(entry), [entry]);
  const coachMessages = entry.coach_messages ?? [];

  return (
    <Layout isAdmin user={{ name: 'Psicologo' }}>
      <div className="w-full space-y-6">
        {breadcrumbs([
          { text: 'Clienti', url: '/therapists/patients' },
          {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            text: patient.display_name!,
            url: `/therapists/patients/${patient.id}`,
          },
          {
            text: 'Diari',
            url: `/therapists/patients/${patient.id}/journals`,
          },
          {
            text: entry.completed_at ? formatDateTime(entry.completed_at) : entry.date,
            current: true,
          },
        ])}

        {entry.safety_blocked && (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              Questo diario è stato bloccato per motivi di sicurezza
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between gap-2">
          <div className="flex flex-col justify-start gap-2">
            <Heading level={2}>{entry.summary?.headline || 'Diario'}</Heading>
            <div className="flex flex-col text-muted-foreground">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                <span>
                  {entry.completed_at
                    ? `Compilato il ${formatDateTime(entry.completed_at)}`
                    : `Diario del ${entry.date}`}
                </span>
              </div>
              {entry.emotion?.label && (
                <div className="flex items-center gap-2">
                  <Thermometer className="w-4 h-4" />
                  <span>
                    Emozione dominante: {entry.emotion.label}
                    {typeof entry.emotion.intensity === 'number'
                      ? ` (${entry.emotion.intensity}/10)`
                      : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col justify-start gap-2">
            {typeof entry.mood_level === 'number' && (
              <GaugePill
                label="Umore"
                value={entry.mood_level}
                icon={<Sparkles className="w-3 h-3" />}
              />
            )}
            {typeof entry.energy_level === 'number' && (
              <GaugePill
                label="Energia"
                value={entry.energy_level}
                icon={<Zap className="w-3 h-3" />}
              />
            )}
          </div>
        </div>

        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList>
            <TabsTrigger value="summary">Riepilogo clinico 5F</TabsTrigger>
            <TabsTrigger value="details">Dettaglio diario</TabsTrigger>
            {entry.safety_blocked && coachMessages.length > 0 && (
              <TabsTrigger value="coach">Sessione coach</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                <ClipboardList className="w-6 h-6" />
                <Heading level={2}>Riepilogo clinico 5F</Heading>
              </div>
            </div>

            {summaryUnavailable ? (
              <div className="rounded-md border border-dashed border-muted-foreground/50 p-6 text-sm text-muted-foreground text-center">
                {entry.summary_unavailable_message || 'Riassunto non disponibile.'}
              </div>
            ) : summaryHasContent && summary ? (
              <div className="space-y-6">
                {summary.narrative && (
                  <Card semantic="primary">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">Descrizione clinica</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p>{summary.narrative}</p>
                    </CardContent>
                  </Card>
                )}

                {summary.emotion && (summary.emotion.label || summary.emotion.intensity) && (
                    <Card semantic="warning">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Thermometer className="w-4 h-4" />
                          Emozione dominante
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm">
                          {summary.emotion.label || '—'}
                          {typeof summary.emotion.intensity === 'number'
                            ? ` • Intensità ${summary.emotion.intensity}/10`
                            : ''}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {summary.fields.length > 0 && (
                  <div className="grid gap-3 md:grid-cols-2">
                    {summary.fields.map((field) => (
                      <div
                        key={field.key}
                        className="rounded-xl border border-muted-foreground/10 bg-background/60 p-4 shadow-sm"
                      >
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
                          {field.label}
                        </div>
                        <div className="text-sm leading-relaxed whitespace-pre-line text-foreground/90">
                          {field.value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {summary.patterns.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                      <ListTree className="w-4 h-4" />
                      Pattern emergenti
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {summary.patterns.map((pattern, index) => (
                        <Badge
                          key={`${pattern}-${index}`}
                          variant="outline"
                          className="border-muted-foreground/30"
                        >
                          {pattern}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-muted-foreground/50 p-6 text-sm text-muted-foreground text-center">
                Nessun dettaglio disponibile nel Summary 5F per questo diario. Il cliente potrebbe
                non aver completato tutti gli slot oppure il riepilogo AI non è stato generato.
              </div>
            )}
          </TabsContent>

          <TabsContent value="details" className="space-y-4">
            <ReviewStep
              entry={reviewEntry}
              showActions={false}
              isConfirming={false}
              title="Dettaglio diario"
              description="Sintesi approvata dal cliente: include i punti principali, le carte confermate e il testo riepilogativo del diario."
              emptyLabel="Il riepilogo condiviso del diario non è disponibile."
            />
          </TabsContent>

          {entry.safety_blocked && coachMessages.length > 0 && (
            <TabsContent value="coach" className="space-y-4">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-destructive" />
                <span className="font-semibold">Sessione coach al momento del blocco</span>
              </div>
              <div className="space-y-3">
                {coachMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <div className="mb-1 text-xs font-semibold opacity-70">
                        {msg.role === 'user' ? 'Paziente' : 'Coach'}
                      </div>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
